import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { BadgeCheck, Mail, Camera, FileText, MapPin, Clock, Phone as PhoneIcon, ScrollText, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import ProTermsModal from "@/components/ProTermsModal";

const ICONS = {
  email_verified: Mail,
  name_complete: FileText, photo_complete: Camera, bio_complete: FileText,
  category_complete: FileText, city_zip_complete: MapPin, radius_complete: MapPin,
  hours_complete: Clock, portfolio_complete: Camera, contact_method_complete: PhoneIcon,
  terms_accepted: ScrollText, account_active: BadgeCheck,
};

const NUDGE_COPY = {
  email_verified: "Confirm your email address.",
  name_complete: "Add your business name.",
  photo_complete: "Add a profile photo or business logo.",
  bio_complete: "Write a short business description.",
  category_complete: "Pick at least one service category.",
  city_zip_complete: "Add your city and ZIP.",
  radius_complete: "Set your service radius.",
  hours_complete: "Add your business hours.",
  portfolio_complete: "Add at least 3 portfolio photos.",
  contact_method_complete: "Add a customer contact method.",
  terms_accepted: "Accept the LIMYA Pro Terms.",
  account_active: "Your account is on hold — contact support.",
};

export default function VerificationChecklist() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  const load = () => api.get("/pros/me/verification").then(r => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const resendEmail = async () => {
    setBusy(true);
    try { await api.post("/auth/resend-verification"); toast.success("Verification email sent — check your inbox."); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not resend"); }
    finally { setBusy(false); }
  };
  const openTerms = () => setShowTerms(true);

  if (!data) return null;
  const { conditions, missing, condition_labels, completed, total, badge_active, email_verified, status, lost_reason } = data;
  const nextKey = (missing || [])[0];
  const remaining = (missing || []).length;
  const nudgeAction = nextKey === "email_verified"
    ? { label: "Resend link", onClick: resendEmail }
    : nextKey === "terms_accepted"
      ? { label: "Review LIMYA Pro Terms", onClick: openTerms }
      : { label: "Complete profile", href: "/dashboard#edit-profile" };

  return (
    <section className="mb-8 bg-white rounded-2xl p-6 shadow-sm border border-black/5" data-testid="verification-checklist">
      {nextKey && (
        <div className="mb-4 flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl bg-[#F7F6F2] border border-black/[0.06] px-4 py-3" data-testid="verification-nudge">
          <div className="flex-1 min-w-0">
            <p className="font-bold text-sm" style={{ fontFamily: "Outfit" }} data-testid="verification-nudge-title">
              {remaining === 1 ? "One step closer to verified" : "Finish your verification"}
            </p>
            <p className="text-sm text-[#2D2926]/65" data-testid="verification-nudge-next">
              {NUDGE_COPY[nextKey] || `${condition_labels[nextKey]}.`}
            </p>
          </div>
          {nudgeAction.href ? (
            <a href={nudgeAction.href} className="text-xs font-semibold text-[#245544] hover:underline shrink-0" data-testid="verification-nudge-action">
              {nudgeAction.label} →
            </a>
          ) : (
            <button onClick={nudgeAction.onClick} disabled={busy}
              className="text-xs font-semibold text-[#245544] hover:underline shrink-0 disabled:opacity-50"
              data-testid="verification-nudge-action">
              {nudgeAction.label} →
            </button>
          )}
        </div>
      )}
      <div className="flex items-center justify-between flex-wrap gap-2 cursor-pointer" onClick={() => setOpen(!open)}>
        <div className="flex items-center gap-2">
          <BadgeCheck size={20} className={badge_active ? "text-[#245544]" : "text-[#222]/30"}/>
          <div>
            <h3 className="text-lg font-bold" style={{ fontFamily: "Outfit" }}>
              {badge_active ? "Verified Profile" : "Profile verification"}
            </h3>
            <p className="text-xs text-[#222]/55">
              {badge_active ? "Fully verified" : "Verification progress"} — {completed} of {total} requirements completed
              {status === "lost" && lost_reason && <span className="text-red-600"> · Lost: {lost_reason}</span>}
              {status === "action_required" && <span className="text-[#8A6412]"> · Action required: current LIMYA Pro Terms need acceptance</span>}
              {status === "suspended" && <span className="text-red-600"> · Hidden while account is suspended</span>}
            </p>
          </div>
        </div>
        <button className="text-xs px-3 py-1.5 rounded-full bg-black/5 flex items-center gap-1" data-testid="verification-toggle">
          {open ? <ChevronUp size={14}/> : <ChevronDown size={14}/>} {open ? "Hide" : "View checklist"}
        </button>
      </div>
      <p className="text-[11px] text-[#222]/45 mt-2">
        Free for every Professional — verification depends only on your profile, never on your membership plan.
        LIMYA automatically verifies your email and profile completeness — not a license, business guarantee, or endorsement.
      </p>

      {open && (
        <div className="mt-4 space-y-2" data-testid="verification-items">
          {Object.entries(condition_labels).map(([key, label]) => {
            const done = conditions?.[key];
            const Icon = ICONS[key] || FileText;
            return (
              <div key={key} className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg ${done ? "bg-[#E9F0EC]" : "bg-black/[0.03]"}`} data-testid={`verification-item-${key}`}>
                <Icon size={14} className={done ? "text-[#245544]" : "text-[#222]/40"}/>
                <span className={done ? "text-[#245544]" : "text-[#222]/70"}>{label}</span>
                {!done && key === "email_verified" && !email_verified && (
                  <button onClick={resendEmail} disabled={busy} className="ml-auto text-xs underline text-[#245544]" data-testid="verification-resend-email">Resend link</button>
                )}
                {!done && key === "terms_accepted" && (
                  <button onClick={openTerms} className="ml-auto text-xs underline text-[#245544]" data-testid="verification-accept-terms">Review LIMYA Pro Terms</button>
                )}
                {!done && ["name_complete", "photo_complete", "bio_complete", "category_complete", "city_zip_complete", "radius_complete", "hours_complete", "portfolio_complete", "contact_method_complete"].includes(key) && (
                  <a href="/dashboard#edit-profile" className="ml-auto text-xs underline text-[#245544]">Edit profile</a>
                )}
              </div>
            );
          })}
        </div>
      )}
      {showTerms && (
        <ProTermsModal onClose={() => setShowTerms(false)} onAccepted={load}/>
      )}
    </section>
  );
}
